import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BRAND } from '@platform/shared';
import { Link, navigate, usePath } from '../lib/router';
import { useMedia } from '../lib/media';
import { toggleTheme, useTheme } from '../lib/theme';
import { usePendingDocuments } from '../stores/documents';
import { useShowPro } from '../stores/pro';
import { toggleSearch } from '../stores/search';
import { logout, useMe } from '../stores/session';
import { InvestMenu } from './InvestMenu';
import { MegaMenu, type MegaColumn, type MegaLink, type NavItem, type Promo } from './MegaMenu';
import { NotificationBell } from './NotificationBell';
import { DemoSwitch } from './DemoAccess';
import { cx, Icon } from './ui';

const LINKS = [
  { to: '/trade', label: 'Trade' },
  { to: '/markets', label: 'Markets' },
  { to: '/portfolio', label: 'Portfolio' },
];
const WALLET: NavItem = { to: '/wallet', label: 'Wallet', icon: 'wallet', prefixes: ['/wallet'] };
const PRO: NavItem = { to: '/pro', label: 'Crypto Pro', icon: 'candles', prefixes: ['/pro'] };
/** The Rewards Hub picture card, at the top of More and in the phone menu's picture row. */
export const REWARDS_PROMO: Promo = { to: '/earn', image: '/menu/rewards-hub.webp', title: 'Rewards Hub', cta: 'Learn more', tone: 'managed' };
/** The less-visited pages, under More so the bar keeps one row. Crypto Pro, and below 1280 px Wallet, join them in a Trade column. */
const MORE: MegaColumn[] = [
  {
    title: 'Hot',
    promo: REWARDS_PROMO,
    links: [
      { to: '/earn/rewards', label: 'Rewards', blurb: 'Your bonuses and perks', glyph: 'gift', prefixes: ['/earn/rewards'] },
      { to: '/earn/referrals', label: 'Invite and earn', blurb: 'Refer friends to the platform', glyph: 'invite', prefixes: ['/earn/referrals'] },
    ],
  },
  {
    title: 'Earn',
    links: [
      { to: '/earn/yield', label: 'Yield', blurb: 'Earn on idle balances', glyph: 'wallet', prefixes: ['/earn/yield'] },
      { to: '/earn/missions', label: 'Missions', blurb: 'Complete tasks for rewards', glyph: 'target', prefixes: ['/earn/missions'] },
      { to: '/earn/leaderboard', label: 'Leaderboard', blurb: 'See the top traders this month', glyph: 'trophy', prefixes: ['/earn/leaderboard'] },
      { to: '/earn/store', label: 'Store', blurb: 'Spend your points', glyph: 'bag', prefixes: ['/earn/store'] },
      { to: '/earn/benefits', label: 'Benefits', blurb: 'Perks for your tier', glyph: 'shieldCheck', prefixes: ['/earn/benefits'] },
    ],
  },
  {
    title: 'Learn & account',
    links: [
      { to: '/markets/calendar', label: 'Economic calendar', blurb: 'Releases that move markets', glyph: 'clock', prefixes: ['/markets/calendar'] },
      { to: '/academy', label: 'Academy', blurb: 'Free trading lessons', glyph: 'cap', prefixes: ['/academy'] },
      { to: '/academy/glossary', label: 'Glossary', blurb: 'Trading terms explained', glyph: 'book', prefixes: ['/academy/glossary'] },
      { to: '/account', label: 'Account', blurb: 'Profile, security and verification', glyph: 'user', prefixes: ['/account'] },
      { to: '/account/support', label: 'Support', blurb: 'Get help from our team', glyph: 'chat', prefixes: ['/account/support'] },
    ],
  },
];
const MORE_TRADE_PRO: MegaLink = { to: PRO.to, label: PRO.label, blurb: 'Advanced crypto charts and orders', glyph: 'candles', prefixes: PRO.prefixes };
const MORE_TRADE_WALLET: MegaLink = { to: WALLET.to, label: WALLET.label, blurb: 'Balances across your accounts', glyph: 'wallet', prefixes: WALLET.prefixes };
/** [label, pill classes, dot class]. Phones show only the dot so the bar stays on one row. */
const KYC_PILL: Record<string, [string, string, string]> = {
  none: ['Verify', 'bg-warn-bg text-warn', 'bg-warn'],
  pending: ['Verifying', 'bg-accent-bg text-accent-fg', 'bg-accent-fg'],
  approved: ['Verified', 'bg-up-bg text-up', 'bg-up'],
  partial: ['Partly verified', 'bg-warn-bg text-warn', 'bg-warn'],
  rejected: ['Action needed', 'bg-down-bg text-down', 'bg-down'],
};

/** Opens the market picker under itself; "/" and Ctrl/⌘ K do the same from anywhere (MarketSearch). Folds to its icon below 2xl. */
function SymbolSearch() {
  return (
    <button
      type="button"
      onClick={(e) => toggleSearch(e.currentTarget)}
      data-search-anchor
      aria-label="Search markets"
      aria-keyshortcuts="/ Control+K Meta+K"
      className="hidden h-[38px] w-[38px] shrink-0 items-center gap-2 rounded-full border border-nav-line bg-nav-pill pr-2 pl-[11px] text-nav-muted hover:text-nav-fg md:flex 2xl:w-60 2xl:pl-3"
    >
      <Icon name="search" size={15} className="shrink-0" />
      <span className="hidden flex-1 text-left text-[13px] 2xl:inline">Search markets</span>
      <kbd className="num hidden rounded-md border border-nav-line px-1.5 text-[11px] 2xl:inline">/</kbd>
    </button>
  );
}

function AccountMenu() {
  const me = useMe();
  const pending = usePendingDocuments();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  if (!me) return null;
  const [label, cls, dot] = KYC_PILL[me.kycStatus] ?? KYC_PILL.none!;
  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={`Account menu, ${label}`}
        title={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-[40px] items-center gap-2 rounded-full bg-nav-pill px-3 text-nav-fg sm:pr-1.5 sm:pl-3.5"
      >
        <Icon name="user" size={17} />
        {/* Phones and the narrow desktop bar (1024–1279 px) show only the dot; the label is in the title. */}
        <span className={cx('rounded-lg px-2 py-0.5 text-[12px] font-semibold max-sm:hidden lg:max-xl:hidden', cls)}>{label}</span>
        <span aria-hidden="true" className={cx('absolute top-2 right-2 size-2 rounded-full ring-2 ring-nav-pill sm:max-lg:hidden xl:hidden', dot)} />
      </button>
      {open && (
        <div className="shadow-pop absolute top-12 right-0 z-40 w-64 rounded-xl border border-line bg-panel p-1.5 text-fg">
          <div className="px-2.5 py-2">
            <div className="truncate font-semibold">{[me.firstName, me.lastName].filter(Boolean).join(' ') || 'My account'}</div>
            <div className="truncate text-xs text-muted">{me.email}</div>
          </div>
          <div className="my-1 h-px bg-line" />
          <MenuItem icon="grid" onClick={() => go('/account')}>
            Dashboard
          </MenuItem>
          <MenuItem icon="user" onClick={() => go('/account/personal')}>
            Personal information
          </MenuItem>
          <MenuItem icon="shield" onClick={() => go('/account/security')}>
            Security
          </MenuItem>
          <MenuItem icon="wallet" onClick={() => go('/account/deposit')}>
            Deposit
          </MenuItem>
          <MenuItem icon="up" onClick={() => go('/account/withdraw')}>
            Withdraw
          </MenuItem>
          <MenuItem icon="docs" onClick={() => go('/account/documents')}>
            Documents
            {pending > 0 && <span className="num ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-warn px-1.5 text-[11px] font-semibold text-white">{pending}</span>}
          </MenuItem>
          <MenuItem icon="star" onClick={() => go('/earn')}>
            Rewards &amp; referrals
          </MenuItem>
          <MenuItem icon="wallet" onClick={() => go('/earn/yield')}>
            Yield
          </MenuItem>
          <MenuItem icon="docs" onClick={() => go('/account/support')}>
            Support
          </MenuItem>
          {(me.role === 'admin' || me.role === 'dealer') && (
            <MenuItem icon="shield" onClick={() => go('/admin')}>
              Admin
            </MenuItem>
          )}
          {/* Below 360px the theme button leaves the bar to keep it on one row. */}
          <div className="min-[360px]:hidden">
            <MenuItem icon={theme === 'dark' ? 'sun' : 'moon'} onClick={() => (setOpen(false), toggleTheme())}>
              {theme === 'dark' ? 'Light theme' : 'Dark theme'}
            </MenuItem>
          </div>
          <div className="my-1 h-px bg-line" />
          <MenuItem icon="logout" onClick={() => (setOpen(false), void logout().then(() => navigate('/login')))}>
            Log out
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, onClick, children }: { icon: Parameters<typeof Icon>[0]['name']; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-raised">
      <Icon name={icon} size={15} className="text-muted" />
      {children}
    </button>
  );
}

function NavLink({ to, label, path }: { to: string; label: string; path: string }) {
  const active = path === to || path.startsWith(to + '/');
  return (
    <Link to={to} className={cx('rounded-full px-2.5 py-2 text-[14px] font-semibold text-nav-fg no-underline 2xl:px-4', active ? 'bg-nav-pill' : 'opacity-80 hover:opacity-100')}>
      {label}
    </Link>
  );
}

export function TopNav() {
  const path = usePath();
  const theme = useTheme();
  const me = useMe();
  const showPro = useShowPro();
  const wide = useMedia('(min-width: 1280px)');
  const trade = [...(showPro ? [MORE_TRADE_PRO] : []), ...(wide ? [] : [MORE_TRADE_WALLET])];
  return (
    <header className="flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-nav-edge bg-nav px-4 py-2.5 text-nav-fg max-[359px]:px-3 sm:gap-x-5 md:px-6 lg:gap-x-2 xl:gap-x-3 2xl:gap-x-5">
      <Link to="/trade" className="flex items-center text-nav-fg no-underline">
        <span className="text-[17px] font-semibold tracking-tight max-[359px]:text-[15px]">{BRAND.name}</span>
      </Link>
      <span className="hidden h-6 w-px bg-nav-line lg:block" />
      <nav aria-label="Main" className="hidden flex-wrap gap-1 lg:flex">
        {/* Trade, Markets, Portfolio, Wallet, Invest, More. Below 1280 px Wallet moves into More. */}
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} label={l.label} path={path} />
        ))}
        {wide && <NavLink to={WALLET.to} label={WALLET.label} path={path} />}
        {/* Copy Trading, Smart Portfolios and Meme coins: one entry, however many are switched on. */}
        <InvestMenu />
        <MegaMenu label="More" columns={trade.length ? [{ title: 'Trade', links: trade }, ...MORE] : MORE} />
      </nav>
      <div className="flex-1" />
      <SymbolSearch />
      {me && <DemoSwitch />}
      <button type="button" onClick={toggleTheme} aria-label="Toggle theme" className={cx('grid size-[38px] place-items-center rounded-full text-nav-fg hover:bg-nav-pill', me && 'max-[359px]:hidden')}>
        <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
      </button>
      {me ? (
        <>
          {/* On phones Deposit lives in the account menu. */}
          <Link to="/account/deposit" className="hidden h-[38px] items-center rounded-full bg-accent px-4 font-semibold text-white no-underline sm:flex">
            Deposit
          </Link>
          <NotificationBell />
          <AccountMenu />
        </>
      ) : (
        <>
          <Link to="/login" className="flex h-[38px] items-center rounded-full px-3 font-semibold text-fg no-underline">
            Log in
          </Link>
          <Link to="/register" className="flex h-[38px] items-center rounded-full bg-accent px-4 font-semibold text-white no-underline">
            Sign up
          </Link>
        </>
      )}
    </header>
  );
}
